
import React, { useCallback } from 'react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

// ── Props ───────────────────────────────────────────────────────────────────

interface GalleryContainerProps {
  id: string
  title?: string
  selected?: boolean
  width?: number
  height?: number
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  totalItems?: number
  mediaType?: string
  onEnterContainer?: () => void
}

// ── Main component ──────────────────────────────────────────────────────────

function GalleryContainerComponent({
  id,
  title,
  selected = false,
  width,
  height,
  onSelect,
  onHoverChange,
  onStateChange,
  state = {},
  totalItems = 0,
  mediaType,
  onEnterContainer,
}: GalleryContainerProps) {
  const seriesName = (state.seriesName as string) || title || 'Gallery Container'
  const mediaLabel = mediaType || 'image'

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onEnterContainer?.()
  }, [onEnterContainer])

  return (
    <NodeContainer
      nodeId={id}
      title={seriesName}
      icon="layers"
      selected={selected}
      nodeType="text"
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent
        className="relative flex flex-col flex-1 p-0 overflow-hidden cursor-pointer"
        onDoubleClick={handleDoubleClick}
      >
        {/* Stats bar */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {totalItems} {totalItems === 1 ? mediaLabel : `${mediaLabel}s`}
          </span>
          {mediaType && (
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded capitalize',
              mediaType === 'image' ? 'bg-blue-500/10 text-blue-400' :
              mediaType === 'video' ? 'bg-purple-500/10 text-purple-400' :
              'bg-muted text-muted-foreground',
            )}>
              {mediaType}
            </span>
          )}
        </div>

        {/* Preview / placeholder area */}
        <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8 px-4">
          {totalItems === 0 ? (
            <>
              <Icon name="layers" size="sm" className="text-muted-foreground/40" />
              <span className="text-xs text-muted-foreground">
                Empty container
              </span>
            </>
          ) : (
            <>
              {/* Stacked cards preview */}
              <div className="relative w-24 h-24">
                <div className="absolute inset-0 rounded-lg bg-muted/60 border border-border/40 transform rotate-3 translate-x-1 translate-y-1" />
                <div className="absolute inset-0 rounded-lg bg-muted/80 border border-border/40 transform -rotate-2 -translate-x-0.5" />
                <div className="absolute inset-0 rounded-lg bg-popover border border-border/60 flex items-center justify-center">
                  <span className="text-lg font-medium text-foreground tabular-nums">{totalItems}</span>
                </div>
              </div>
              <span className="text-xs text-muted-foreground">
                Double-click to open
              </span>
            </>
          )}
        </div>
      </CardContent>
    </NodeContainer>
  )
}

export const GalleryContainer = withNodeOptimization(GalleryContainerComponent, {
  compareProps: ['id', 'title', 'selected', 'state', 'totalItems', 'mediaType'],
})
